import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { ITraining, BaseVideoFormData } from '../../types';
import { apiService } from '../../api/api.service';

interface FreeVideoState {
    videos: ITraining[];
    selectedVideo: ITraining | null;
    isLoading: boolean;
    error: string | null;
    deleteDialog: {
        open: boolean;
        videoId: number | null;
    };
    openDialog: boolean;
}

const initialState: FreeVideoState = {
    videos: [],
    selectedVideo: null,
    isLoading: false,
    error: null,
    deleteDialog: {
        open: false,
        videoId: null
    },
    openDialog: false
};

// Async thunks
export const fetchFreeVideo = createAsyncThunk(
    'freeVideo/fetch',
    async (language: string, { rejectWithValue }) => {
        try {
            const data = await apiService.getVideoList('free');
            return {
                data,
                language
            };
        } catch (error) {
            return rejectWithValue('Failed to fetch free video');
        }
    }
);

export const uploadFreeVideo = createAsyncThunk(
    'freeVideo/upload',
    async ({
        training,
        language,
        videoFile,
        thumbnailFile
    }: {
        training: BaseVideoFormData['training'],
        language: string,
        videoFile?: File,
        thumbnailFile?: File
    }, { rejectWithValue }) => {
        try {
            let video_file_path = undefined;
            let preview_file_path = undefined;

            // Загружаем видео файл, если он есть
            if (videoFile) {
                const videoResponse = await apiService.uploadFreeVideoFile(videoFile);
                video_file_path = videoResponse.file_path;
            }

            // Загружаем превью файл, если он есть
            if (thumbnailFile) {
                const previewResponse = await apiService.uploadFreePreviewFile(thumbnailFile);
                preview_file_path = previewResponse.file_path;
            }

            const videoData = {
                ...training,
                language: language.toUpperCase(),
                title: `free_video_${language.toLowerCase()}`,
                description: `free_video_${language.toLowerCase()}`,
                video_file_path,
                preview_file_path
            };

            const response = await apiService.uploadVideo('free', videoData);
            return response;
        } catch (error) {
            return rejectWithValue('Failed to upload free video');
        }
    }
);

export const editFreeVideo = createAsyncThunk(
    'freeVideo/edit',
    async ({
        id,
        training,
        language,
        videoFile,
        thumbnailFile
    }: {
        id: number;
        training: BaseVideoFormData['training'],
        language: string,
        videoFile?: File,
        thumbnailFile?: File
    }, { rejectWithValue }) => {
        try {
            let video_file_path = undefined;
            let preview_file_path = undefined;

            // Загружаем видео файл, если он есть
            if (videoFile) {
                const videoResponse = await apiService.uploadFreeVideoFile(videoFile);
                video_file_path = videoResponse.file_path;
            }

            // Загружаем превью файл, если он есть
            if (thumbnailFile) {
                const previewResponse = await apiService.uploadFreePreviewFile(thumbnailFile);
                preview_file_path = previewResponse.file_path;
            }

            const videoData = {
                ...training,
                language: language.toUpperCase(),
                title: `free_video_${language.toLowerCase()}`,
                description: `free_video_${language.toLowerCase()}`,
                video_file_path,
                preview_file_path
            };

            const response = await apiService.editVideo('free', id, videoData);
            return response;
        } catch (error) {
            return rejectWithValue('Failed to edit free video');
        }
    }
);

export const deleteFreeVideo = createAsyncThunk(
    'freeVideo/delete',
    async (id: number, { rejectWithValue }) => {
        try {
            await apiService.deleteVideo('free', id);
            return id;
        } catch (error) {
            return rejectWithValue('Failed to delete free video');
        }
    }
);

const freeVideoSlice = createSlice({
    name: 'freeVideo',
    initialState,
    reducers: {
        setSelectedVideo: (state, action: PayloadAction<ITraining | null>) => {
            state.selectedVideo = action.payload;
        },
        setOpenDialog: (state, action: PayloadAction<boolean>) => {
            state.openDialog = action.payload;
        },
        setDeleteDialog: (state, action: PayloadAction<{ open: boolean; videoId: number | null }>) => {
            state.deleteDialog = action.payload;
        },
        updateVideoFromStatus: (state, action: PayloadAction<{
            videoId: number;
            updates: Partial<ITraining>
        }>) => {
            const { videoId, updates } = action.payload;

            const videoIndex = state.videos.findIndex(v => v.id === videoId);
            if (videoIndex !== -1) {
                state.videos[videoIndex] = {
                    ...state.videos[videoIndex],
                    ...updates
                };
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // fetchFreeVideo
            .addCase(fetchFreeVideo.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchFreeVideo.fulfilled, (state, action) => {
                const { data, language } = action.payload;
                state.videos = data.filter(
                    video => video.language && video.language.toUpperCase() === language.toUpperCase()
                );
                state.isLoading = false;
            })
            .addCase(fetchFreeVideo.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })

            // uploadFreeVideo
            .addCase(uploadFreeVideo.fulfilled, (state, action) => {
                state.videos = [action.payload];
                state.openDialog = false;
            })

            // editFreeVideo
            .addCase(editFreeVideo.fulfilled, (state, action) => {
                state.videos = [action.payload];
                state.openDialog = false;
            })

            // deleteFreeVideo
            .addCase(deleteFreeVideo.fulfilled, (state, action) => {
                state.videos = [];
                state.deleteDialog = {
                    open: false,
                    videoId: null
                };
            });
    }
});

export const {
    setSelectedVideo,
    setOpenDialog,
    setDeleteDialog,
    updateVideoFromStatus
} = freeVideoSlice.actions;

export default freeVideoSlice.reducer; 